import React, { useEffect, useMemo, useState } from "react";
import * as Mui from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import * as Icons from "react-feather";
import { useDispatch, useSelector } from "react-redux";
import * as Router from "react-router-dom";
import { DashboardLayout } from "component/layout/dashboard";
import { FullPageCard } from "component/shared/full-page-card";
import { FeatherIcon } from "component/shared/feather-icon";
import { PhotoManager } from "component/shared/photo-manager";
import { PageProps } from "shared/page-props";
import { RootState } from "redux/store";
import {
	getOffMarketListingById,
	getOffMarketListingFlags,
	getOffMarketListingsLoaded,
	getAgents,
} from "redux/selector";
import {
	fetchOffMarketListings,
	createOffMarketListing,
	updateOffMarketListing,
} from "redux/slice/off-market-listings";
import { UpsertOffMarketListingResource } from "api/resource/off-market-listing";
import { coldFusionReplatform } from "shared/env";
import * as env from "shared/env";
import * as paths from "routes/paths";
import { OffMarketForm } from "./off-market-form";

interface Props extends PageProps, Router.RouteComponentProps {}

const TITLE = "Off-Market Listings";

const openDetail = (detailUrl: string) => {
	const url = /^https?:\/\//i.test(detailUrl) ? detailUrl : `${env.coldFusionUrl}${detailUrl}`;
	window.open(url, "_blank");
};

// CF's edit header summary is "$<ListPrice> | Status: <status>" (soldsmodify_output_1.cfm:78-79).
const formatPrice = (price: number | null): string => (price == null ? "" : `$${price.toLocaleString()}`);

const OffMarketEditBase: React.FC<Props> = ({ user, history, match, location }) => {
	const dispatch = useDispatch();
	const idParam = (match.params as { id?: string }).id;
	const id = idParam ? Number(idParam) : undefined;
	const isNew = id === undefined;

	const loaded = useSelector((state: RootState) => getOffMarketListingsLoaded(state));
	const flags = useSelector((state: RootState) => getOffMarketListingFlags(state));
	const agentResources = useSelector((state: RootState) => getAgents(state)) || [];
	const property = useSelector((state: RootState) =>
		id != null ? getOffMarketListingById(state, id) : undefined
	);

	const agentOptions = useMemo(
		() =>
			agentResources.map((agent) => ({
				id: agent.id,
				name: [agent.lastName, agent.firstName].filter(Boolean).join(", ") || `Agent ${agent.id}`,
			})),
		[agentResources]
	);

	// MAX-4194: seed the free-text agent box from a stored AID when no typed name exists. Same
	// rationale as the supplemental page — see listings-supplemental/edit.tsx for the full note,
	// including why agentOptions ("Last, First", a display format) cannot supply this.
	//
	// Note the page calls the record `property` while the form's prop is named `listing`.
	const agentNameSeed = useMemo(() => {
		if (!property || property.agentName || (property.agentId ?? 0) <= 0) {
			return "";
		}
		const assigned = agentResources.find((agent) => agent.id === property.agentId);
		return assigned ? [assigned.firstName, assigned.lastName].filter(Boolean).join(" ") : "";
	}, [property, agentResources]);

	const [saving, setSaving] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [savedMessage, setSavedMessage] = useState<string | null>(null);
	const [tab, setTab] = useState(0);

	// Post-create nav lands here with ?message= (CF returns to the new record's photo tab); show it once.
	useEffect(() => {
		const message = new URLSearchParams(location.search).get("message");
		if (message) {
			setSavedMessage(message);
		}
	}, [location.search]);

	// Off-market rows + client flags (office account, FeatureAgentOffice) arrive only with the list
	// fetch, so a direct load / refresh of this route needs the list loaded to render the record and
	// agent block correctly. Navigating in from the list already has them in state.
	useEffect(() => {
		if (!loaded) {
			dispatch(fetchOffMarketListings() as any);
		}
	}, [loaded, dispatch]);

	const goToList = (message?: string) =>
		history.push(
			message
				? `${paths.listingsOffMarket}?message=${encodeURIComponent(message)}`
				: paths.listingsOffMarket
		);

	// MAX-4194: returns whether the save actually landed. The form re-baselines its agent-name
	// dirty check on success, and this function catches its own errors — so awaiting it is not
	// on its own evidence that anything was written.
	const handleSave = async (body: UpsertOffMarketListingResource): Promise<boolean> => {
		setSaving(true);
		setError(null);
		try {
			if (isNew) {
				// F4: .unwrap() so an API failure rejects here rather than resolving and navigating away.
				const created = await dispatch(createOffMarketListing({ body }) as any).unwrap();
				setSaving(false);
				// CF returns to the new record's photo tab after create (soldsmodify_update.cfm:130-138);
				// the native editor renders the Photos card inline, so land on that record's edit page.
				history.push(
					`${Router.generatePath(paths.listingsOffMarketEdit, { id: created.id })}?message=${encodeURIComponent("Sold/Pending listing created")}`
				);
				return true;
			} else {
				await dispatch(updateOffMarketListing({ id: id as number, body }) as any).unwrap();
				setSaving(false);
				// CF stays on the still-open edit form after update — show the success message in place.
				setSavedMessage("Modifications Successful");
				return true;
			}
		} catch{
			setSaving(false);
			setError(isNew ? "Failed to create listing" : "Failed to save listing");
			return false;
		}
	};

	// On edit, wait for the record before mounting the form so fields aren't briefly blank.
	const loadingProperty = !isNew && !property;

	return (
		<DashboardLayout
			permitted={user && user.permissions.soldPendingListings}
			title={TITLE}
			header={
				<Mui.Typography variant="h1">
					<FeatherIcon>
						<Icons.Home />
					</FeatherIcon>
					{TITLE}
				</Mui.Typography>
			}
		>
			{/* Add-mode instructional sentence (soldsmodify_output_1.cfm:69-73). */}
			{isNew && (
				<Mui.Box mb={2}>
					<Mui.Typography variant="body2" color="textSecondary">
						This function should be used for properties that are no longer on the market and do not
						show up in your Sold/Pendings list.
					</Mui.Typography>
				</Mui.Box>
			)}

			{/* Address title + $ListPrice/Status summary, with View Property Page. Edit mode only. */}
			{property && (
				<Mui.Box mb={2} display="flex" justifyContent="space-between" alignItems="flex-start">
					<div>
						<Mui.Typography variant="h6">
							{[property.streetNumber, property.streetName].filter(Boolean).join(" ")}
							{" , "}
							{(property.city || "").toUpperCase()}, {property.state} {property.zip}
						</Mui.Typography>
						<Mui.Typography variant="body2" color="textSecondary">
							{formatPrice(property.listPrice)} | Status: {property.statusLabel}
						</Mui.Typography>
					</div>
					{property.detailUrl && (
						<Mui.Button variant="contained" onClick={() => openDetail(property.detailUrl)}>
							View Property Page
						</Mui.Button>
					)}
				</Mui.Box>
			)}

			{/* CF's editor is a two-tab layout: Listing Information | Photos (MAX-3939). The Photos tab only
			    exists once the listing has been saved (CF hides it in add mode), so a brand-new listing
			    shows just the form. Both panels stay mounted (hidden when inactive) so unsaved edits survive
			    a tab switch. */}
			{isNew ? (
				<OffMarketForm
					listing={null}
					flags={{ agentControl: flags.agentControl }}
					agentNameSeed={agentNameSeed}
					agents={agentOptions}
					saving={saving}
					onSave={handleSave}
					onCancel={() => goToList()}
				/>
			) : loadingProperty ? (
				<Mui.Box mb={2}>
					<FullPageCard>
						<Mui.CircularProgress />
					</FullPageCard>
				</Mui.Box>
			) : (
				<>
					<Mui.Tabs
						value={tab}
						onChange={(_e, value) => setTab(value)}
						indicatorColor="primary"
						textColor="primary"
					>
						<Mui.Tab label="Listing Information" />
						<Mui.Tab label="Photos" />
					</Mui.Tabs>

					<Mui.Box mt={2} hidden={tab !== 0}>
						<OffMarketForm
							key={id}
						listing={property || null}
							flags={{ agentControl: flags.agentControl }}
							agentNameSeed={agentNameSeed}
							agents={agentOptions}
							saving={saving}
							onSave={handleSave}
							onCancel={() => goToList()}
						/>
					</Mui.Box>

					<Mui.Box mt={2} hidden={tab !== 1}>
						{property && (
							<FullPageCard>
								<PhotoManager
									listingKey={{ clientPropertyId: property.clientPropertyId }}
									hasBoard={property.boardId !== 0}
									inline
								/>
							</FullPageCard>
						)}
					</Mui.Box>
				</>
			)}

			<Mui.Snackbar open={!!error} autoHideDuration={4000} onClose={() => setError(null)}>
				<MuiAlert severity="error">
					<Mui.Typography>{error}</Mui.Typography>
				</MuiAlert>
			</Mui.Snackbar>

			<Mui.Snackbar open={!!savedMessage} autoHideDuration={4000} onClose={() => setSavedMessage(null)}>
				<MuiAlert severity="success">
					<Mui.Typography>{savedMessage}</Mui.Typography>
				</MuiAlert>
			</Mui.Snackbar>
		</DashboardLayout>
	);
};

const OffMarketEditPageBase: React.FC<Props> = (props) => {
	// Native edit route only exists under the replatform flag; otherwise CF owns the editor inside its
	// own iframe, so send deep links back to the list route (which renders the CF iframe when off).
	if (!coldFusionReplatform) {
		return <Router.Redirect to={paths.listingsOffMarket} />;
	}
	return <OffMarketEditBase {...props} />;
};

export const OffMarketEditPage = OffMarketEditPageBase;
